import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../store/authStore";
import { useQueryStore } from "../store/queryStore";
import { authService } from "../services/authService";
import { queryService } from "../services/queryService";
import { disconnectEcho } from "../services/echoService";
import toast from "react-hot-toast";
import {
  FileText,
  MessageSquareText,
  GitBranch,
  FileCheck,
  TrendingUp,
  Users,
  Bell,
  Settings,
  Sparkles,
  Zap,
  Target,
  BarChart3,
  LogOut,
  ChevronDown,
  ChevronUp,
  Filter,
  IndianRupee,
  Ban, 
  ReceiptIndianRupee ,
  FileSpreadsheet,
  Calendar,
  X,
  XCircle,
  RefreshCw,
} from "lucide-react";

function Home() {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const counts = useQueryStore((state) => state.counts);
  const setCounts = useQueryStore((state) => state.setCounts);
  const [activeNotifications] = useState(0);
  const [recentActivities, setRecentActivities] = useState([]);
  const [showAllActivities, setShowAllActivities] = useState(false);
  const [loadingActivities, setLoadingActivities] = useState(true);
  const [expandedActivity, setExpandedActivity] = useState(null);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [dateFilter, setDateFilter] = useState('MTD');
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [isStatsCollapsed, setIsStatsCollapsed] = useState(false);
  const [showMonthlyBreakup, setShowMonthlyBreakup] = useState(false);
  const [selectedBucket, setSelectedBucket] = useState(null);
  const [monthlyData, setMonthlyData] = useState(null);
  const [loadingMonthly, setLoadingMonthly] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    loadCounts();
    loadRecentActivity();
    loadDashboardStats();
  }, [dateFilter]);

  const loadCounts = async () => {
    try {
      const data = await queryService.getQueryCounts();
      setCounts(data);
    } catch (error) {
      console.error("Failed to load counts:", error);
    }
  };

  const loadRecentActivity = async () => {
    try {
      setLoadingActivities(true);
      const response = await queryService.getRecentActivity();
      if (response.success) {
        // Handle both paginated and non-paginated responses
        const data = response.data?.data || response.data || [];
        setRecentActivities(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error("Failed to load recent activity:", error);
      setRecentActivities([]);
    } finally {
      setLoadingActivities(false);
    }
  };

  const loadDashboardStats = async () => {
    try {
      setLoadingStats(true);
      const params = {};
      
      if (dateFilter === 'Custom' && customStartDate && customEndDate) {
        params.date_filter = 'custom';
        params.range_start = customStartDate;
        params.range_end = customEndDate;
      } else if (dateFilter !== 'Custom') {
        params.date_filter = dateFilter;
      }
      
      const response = await queryService.getDashboardStats(params);
      if (response.success) {
        setDashboardStats(response.data);
      }
    } catch (error) {
      console.error("Failed to load dashboard stats:", error);
      setDashboardStats(null);
    } finally {
      setLoadingStats(false);
    }
  };

  const handleApplyCustomDateRange = () => {
    if (customStartDate && customEndDate) {
      setShowDatePicker(false);
      setShowFilterMenu(false);
      loadDashboardStats();
    }
  };

  const handleBucketClick = async (bucketKey, bucketLabel) => {
    setSelectedBucket({ key: bucketKey, label: bucketLabel });
    setShowMonthlyBreakup(true);
    setLoadingMonthly(true);
    
    try {
      const params = {};
      
      if (dateFilter === 'Custom' && customStartDate && customEndDate) {
        params.date_filter = 'custom';
        params.range_start = customStartDate;
        params.range_end = customEndDate;
      } else if (dateFilter !== 'Custom') {
        params.date_filter = dateFilter;
      }
      
      const response = await queryService.getMonthlyBreakup(bucketKey, params);
      if (response.success) {
        setMonthlyData(response.data);
      }
    } catch (error) {
      console.error('Failed to load monthly breakup:', error);
      toast.error(error.response?.data?.message || 'Failed to load monthly breakup');
      setMonthlyData(null);
    } finally {
      setLoadingMonthly(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const params = {};
      
      if (dateFilter === 'Custom' && customStartDate && customEndDate) {
        params.date_filter = 'custom';
        params.range_start = customStartDate;
        params.range_end = customEndDate;
      } else if (dateFilter !== 'Custom') {
        params.date_filter = dateFilter;
      }
      
      await queryService.clearCache(params);
      await loadDashboardStats();
      toast.success('Dashboard refreshed successfully');
    } catch (error) {
      console.error('Failed to refresh:', error);
      toast.error(error.response?.data?.message || 'Failed to refresh dashboard');
    } finally {
      setTimeout(() => setIsRefreshing(false), 500);
    }
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      disconnectEcho();
      logout();
      navigate("/login");
    }
  };

  const menuItems = [
    {
      id: "doc-upload",
      title: "App Status & Doc Upload",
      // description: "Upload application documents",
      description:"",
      icon: FileText,
      gradient: "from-blue-500 to-cyan-500",
      route: "/doc-upload",
      count: null,
    },
    {
      id: "queries",
      title: "Queries",
      description: "Handle application queries",
      icon: MessageSquareText,
      gradient: "from-orange-500 to-amber-500",
      route: "/queries",
      count: counts.raised_to_you?.pending || 0,
    },
    {
      id: "deviations",
      title: "Pening Deviations",
      description: "Review deviations",
      icon: GitBranch,
      gradient: "from-purple-500 to-pink-500",
      route: "/deviations",
      count: null,
    },
    {
      id: "requests",
      title: "Requests",
      description: "Pending requests",
      icon: FileCheck,
      gradient: "from-green-500 to-emerald-500",
      route: "/requests",
      count: null,
    },
    {
      id: "analytics",
      title: "Analytics",
      description: "Performance insights",
      icon: TrendingUp,
      gradient: "from-indigo-500 to-blue-500",
      route: "/analytics",
      count: null,
    },
    {
      id: "team",
      title: "Team",
      description: "Manage your team",
      icon: Users,
      gradient: "from-rose-500 to-red-500",
      route: "/team",
      count: null,
    },
  ];

  const totalStats = {
    label: "Total Applications",
    value: loadingStats ? "..." : (dashboardStats?.TOTAL?.count || 0),
    subValue: loadingStats ? "..." : (dashboardStats?.TOTAL?.formatted_amount || "₹0.00"),
    icon: FileSpreadsheet,
    gradient: "from-indigo-500 to-purple-500",
    bgGradient: "from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20",
    iconColor: "text-indigo-600 dark:text-indigo-400",
  };

  const quickStats = [
    {
      label: "Lead",
      value: loadingStats ? "..." : (dashboardStats?.LAF?.count || 0),
      subValue: loadingStats ? "..." : (dashboardStats?.LAF?.formatted_amount || "₹0.00"),
      icon: Target,
      gradient: "from-green-500 to-emerald-500",
      bgGradient: "from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20",
      iconColor: "text-green-600 dark:text-green-400",
    },
    {
      label: "ABND",
      value: loadingStats ? "..." : (dashboardStats?.ABND?.count || 0),
      subValue: loadingStats ? "..." : (dashboardStats?.ABND?.formatted_amount || "₹0.00"),
      icon: IndianRupee,
      gradient: "from-red-500 to-rose-500",
      bgGradient: "from-red-50 to-rose-50 dark:from-red-900/20 dark:to-rose-900/20",
      iconColor: "text-red-600 dark:text-red-400",
    },
    {
      label: "Disbursed",
      value: loadingStats ? "..." : (dashboardStats?.DISBURSED?.count || 0),
      subValue: loadingStats ? "..." : (dashboardStats?.DISBURSED?.formatted_amount || "₹0.00"),
      icon: ReceiptIndianRupee,
      gradient: "from-orange-500 to-amber-500",
      bgGradient: "from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20",
      iconColor: "text-orange-600 dark:text-orange-400",
    },
    {
      label: "Rejected",
      value: loadingStats ? "..." : (dashboardStats?.REJECTED?.count || 0),
      subValue: loadingStats ? "..." : (dashboardStats?.REJECTED?.formatted_amount || "₹0.00"),
      icon: Ban,
      gradient: "from-gray-500 to-slate-500",
      bgGradient: "from-gray-50 to-slate-50 dark:from-gray-900/20 dark:to-slate-900/20",
      iconColor: "text-gray-600 dark:text-gray-400",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 transition-colors duration-300 pt-2">
      {/* Header with Glassmorphism */}
      <div className="backdrop-blur-xl bg-white/80 dark:bg-gray-900/80 border-b border-white/20 dark:border-gray-800 shadow-lg transition-colors duration-300">
        <div className={`px-4 safe-top transition-all duration-300 ${isStatsCollapsed ? 'py-2' : 'py-3'}`}>
          <div className={`flex items-center justify-between ${isStatsCollapsed ? 'mb-0' : 'mb-2'}`}>
            <div className="flex items-center gap-2">
              <img
                src="/logo_light.png"
                alt="Galaxy Logo"
                className="h-6 w-auto dark:hidden"
              />
              <img
                src="/logo_dark.png"
                alt="Galaxy Logo"
                className="h-6 w-auto hidden dark:block"
              />
              <div>
                {/* <h1 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  Galaxy
                </h1> */}
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Welcome, {user?.name?.split(" ")[0]}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate("/notifications")}
                className="relative p-3 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-2xl shadow-lg hover:shadow-xl transition-all active:scale-95"
              >
                <Bell className="w-5 h-5 text-white" />
                {activeNotifications > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold animate-pulse">
                    {activeNotifications}
                  </span>
                )}
              </button>
              <button
                onClick={() => navigate("/settings")}
                className="p-3 bg-white dark:bg-gray-800 rounded-2xl shadow-lg hover:shadow-xl transition-all active:scale-95 border border-gray-100 dark:border-gray-700"
              >
                <Settings className="w-5 h-5 text-gray-700 dark:text-gray-200" />
              </button>
              <button
                onClick={handleLogout}
                className="p-3 bg-gradient-to-br from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 rounded-2xl shadow-lg hover:shadow-xl transition-all active:scale-95"
              >
                <LogOut className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>

          {/* Filter Button */}
          <div className={`relative flex items-center justify-between ${isStatsCollapsed ? 'mb-0' : 'mb-2'}`}>
            {!isStatsCollapsed && (
              <button
                onClick={() => setShowFilterMenu(!showFilterMenu)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-lg font-semibold text-xs shadow-lg hover:shadow-xl transition-all active:scale-95"
              >
                <Filter className="w-3.5 h-3.5" />
                {dateFilter === 'Custom' && customStartDate && customEndDate 
                  ? `${customStartDate} to ${customEndDate}` 
                  : dateFilter}
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showFilterMenu ? 'rotate-180' : ''}`} />
              </button>
            )}
            
            <button
              onClick={() => setIsStatsCollapsed(!isStatsCollapsed)}
              className={`p-1 bg-gray-200 dark:bg-gray-700 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-all active:scale-95 ${isStatsCollapsed ? 'mx-auto' : ''}`}
            >
              <ChevronUp className={`w-3 h-3 text-gray-600 dark:text-gray-300 transition-transform duration-300 ${isStatsCollapsed ? 'rotate-180' : ''}`} />
            </button>
            
            {!isStatsCollapsed && (
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="p-1.5 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-lg shadow-lg hover:shadow-xl transition-all active:scale-95 disabled:opacity-50"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
              </button>
            )}
            
            {showFilterMenu && (
              <div className="absolute top-full left-0 mt-2 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 py-2 z-30 min-w-[120px]">
                {['MTD', 'FTD', 'YTD'].map((filter) => (
                  <button
                    key={filter}
                    onClick={() => {
                      setDateFilter(filter);
                      setShowFilterMenu(false);
                    }}
                    className={`w-full px-4 py-2 text-left text-sm font-medium transition-colors ${
                      dateFilter === filter
                        ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    {filter}
                  </button>
                ))}
                <button
                  onClick={() => {
                    setDateFilter('Custom');
                    setShowFilterMenu(false);
                    setShowDatePicker(true);
                  }}
                  className={`w-full px-4 py-2 text-left text-sm font-medium transition-colors flex items-center gap-2 ${
                    dateFilter === 'Custom'
                      ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <Calendar className="w-4 h-4" />
                  Custom Range
                </button>
              </div>
            )}
          </div>

          {/* Custom Date Picker Modal */}
          {showDatePicker && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-sm w-full shadow-2xl">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">Custom Date Range</h3>
                  <button
                    onClick={() => setShowDatePicker(false)}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      min={customStartDate}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={() => setShowDatePicker(false)}
                      className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleApplyCustomDateRange}
                      disabled={!customStartDate || !customEndDate}
                      className="flex-1 px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Quick Stats - Collapsible */}
          <div className={`overflow-hidden transition-all duration-300 ${isStatsCollapsed ? 'max-h-0 opacity-0' : 'max-h-[500px] opacity-100'}`}>
            {/* Total Stats - Full Width */}
            <div 
              className={`bg-gradient-to-br ${totalStats.bgGradient} rounded-xl p-3 shadow-md border border-white/50 dark:border-gray-700/50 hover:shadow-xl transition-all mt-2`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 bg-gradient-to-br ${totalStats.gradient} rounded-lg shadow-sm`}>
                    <totalStats.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className={`text-[10px] font-bold ${totalStats.iconColor} uppercase tracking-wide`}>
                      {totalStats.label}
                    </p>
                    {totalStats.subValue && (
                      <p className="text-xs font-bold text-gray-800 dark:text-gray-200">
                        {totalStats.subValue}
                      </p>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-extrabold text-gray-900 dark:text-white">
                    {totalStats.value}
                  </p>
                </div>
              </div>
            </div>

            {/* Other Stats - 2x2 Grid */}
            <div className="grid grid-cols-2 gap-2 mt-2">
              {quickStats.map((stat, index) => {
                const bucketKey = stat.label === 'Lead' ? 'APP_CREATION' : stat.label.toUpperCase();
                return (
                  <div
                    key={index}
                    onClick={() => handleBucketClick(bucketKey, stat.label)}
                    className={`bg-gradient-to-br ${stat.bgGradient} rounded-xl p-3 shadow-md border border-white/50 dark:border-gray-700/50 hover:shadow-xl transition-all hover:scale-105 cursor-pointer`}
                  >
                  <div className="flex items-center justify-between mb-2">
                    <div className={`p-1.5 bg-gradient-to-br ${stat.gradient} rounded-lg shadow-sm`}>
                      <stat.icon className="w-4 h-4 text-white" />
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-extrabold text-gray-900 dark:text-white">
                        {stat.value}
                      </p>
                    </div>
                  </div>
                  <p className={`text-[9px] font-bold ${stat.iconColor} uppercase tracking-wide mb-0.5`}>
                    {stat.label}
                  </p>
                  {stat.subValue && (
                    <p className="text-[11px] font-bold text-gray-800 dark:text-gray-200">
                      {stat.subValue}
                    </p>
                  )}
                </div>
              );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Monthly Breakup Modal */}
      {showMonthlyBreakup && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 max-w-md w-full shadow-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white">
                  {selectedBucket?.label} - Monthly Breakup
                </h3>
                <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                  {dateFilter === 'Custom' && customStartDate && customEndDate 
                    ? `${customStartDate} to ${customEndDate}` 
                    : dateFilter}
                </p>
              </div>
              <button
                onClick={() => setShowMonthlyBreakup(false)}
                className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            
            {loadingMonthly ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            ) : monthlyData?.monthly_breakup && Object.keys(monthlyData.monthly_breakup).length > 0 ? (
              <>
                {/* Scrollable Month Cards */}
                <div className="space-y-1.5 max-h-[280px] overflow-y-auto pr-1">
                  {Object.entries(monthlyData.monthly_breakup).map(([monthName, monthData], index) => (
                    <div 
                      key={index}
                      className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700/50 dark:to-gray-800/50 rounded-lg p-2 border border-gray-200 dark:border-gray-600"
                    >
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-1.5">
                          <Calendar className="w-3 h-3 text-indigo-600 dark:text-indigo-400" />
                          <h4 className="text-xs font-bold text-gray-900 dark:text-white">
                            {monthName}
                          </h4>
                        </div>
                        <span className="px-1.5 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 text-[10px] font-bold rounded-full">
                          {monthData.percentage?.toFixed(1)}%
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <p className="text-[9px] text-gray-500 dark:text-gray-400 mb-0.5">Count</p>
                          <p className="text-sm font-bold text-gray-900 dark:text-white">
                            {monthData.count}
                          </p>
                        </div>
                        <div>
                          <p className="text-[9px] text-gray-500 dark:text-gray-400 mb-0.5">Amount</p>
                          <p className="text-[11px] font-bold text-gray-900 dark:text-white">
                            ₹{Number(monthData.amount).toLocaleString('en-IN')}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Total Summary */}
                <div className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-lg p-2.5 border-2 border-indigo-200 dark:border-indigo-800 mt-2">
                  <h4 className="text-[10px] font-bold text-indigo-700 dark:text-indigo-300 mb-1.5">Total Summary</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <p className="text-[9px] text-gray-600 dark:text-gray-400 mb-0.5">Total Count</p>
                      <p className="text-base font-extrabold text-gray-900 dark:text-white">
                        {monthlyData.total_count}
                      </p>
                    </div>
                    <div>
                      <p className="text-[9px] text-gray-600 dark:text-gray-400 mb-0.5">Total Amount</p>
                      <p className="text-[11px] font-extrabold text-gray-900 dark:text-white">
                        ₹{Number(monthlyData.total_amount).toLocaleString('en-IN')}
                      </p>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500 dark:text-gray-400">
                  {dateFilter === 'MTD' 
                    ? 'No monthly breakup available for Month-to-Date filter'
                    : 'No data available for the selected period'}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="px-6 py-6 pb-32">
        {/* Loan Journey Banner */}
        <div
          className="mb-6 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-600 rounded-2xl p-4 shadow-lg relative overflow-hidden cursor-pointer hover:shadow-xl transition-all duration-300"
          onClick={() => {
            const isDev = import.meta.env.MODE === "development";
            const loanUrl = isDev
              ? "https://los.dmsdemo.autovertplug.com/login"
              : "https://los.dms.autovertplug.com/login";
            window.open(loanUrl, "_blank");
          }}
        >
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full -ml-12 -mb-12"></div>
          <div className="relative z-10 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-yellow-300" />
              <h3 className="text-white font-semibold text-base">
                Onboard New Loan
              </h3>
            </div>
            <button className="px-4 py-2 bg-white text-emerald-600 rounded-xl font-semibold text-sm shadow-lg hover:shadow-xl transition-all active:scale-95">
              Start
            </button>
          </div>
        </div>

        {/* AI Assistant Banner */}
        <div className="mb-6 bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 rounded-3xl p-6 shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -mr-20 -mt-20"></div>
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full -ml-16 -mb-16"></div>
          <div className="relative z-10 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-6 h-6 text-yellow-300" />
                <h3 className="text-white font-bold text-lg">AI Assistant</h3>
              </div>
              <p className="text-white/90 text-sm">
                Need help? Ask me anything!
              </p>
              <p className="text-white/90 text-sm">
                Coming soon...
              </p>
            </div>
            <button
              onClick={() => navigate("/ai-assistant")}
              className="px-6 py-3 bg-white text-purple-600 rounded-2xl font-semibold shadow-xl hover:shadow-2xl transition-all active:scale-95"
            >
              Chat
            </button>
          </div>
        </div>

        {/* Menu Grid */}
        <div className="grid grid-cols-2 gap-4">
          {menuItems.map((item, index) => (
            <div
              key={item.id}
              onClick={() => navigate(item.route)}
              className="group relative bg-white dark:bg-gray-800 rounded-3xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 active:scale-95 cursor-pointer border border-gray-100 dark:border-gray-700 overflow-hidden"
              style={{
                animationDelay: `${index * 100}ms`,
                animation: "fadeInUp 0.6s ease-out forwards",
              }}
            >
              {/* Gradient Background Overlay */}
              <div
                className={`absolute inset-0 bg-gradient-to-br ${item.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}
              ></div>

              {/* Icon Container */}
              <div
                className={`relative w-16 h-16 bg-gradient-to-br ${item.gradient} rounded-2xl flex items-center justify-center mb-4 shadow-lg group-hover:shadow-xl transition-all group-hover:scale-110 duration-300`}
              >
                <item.icon className="w-8 h-8 text-white" />
                {item.count > 0 && (
                  <span className="absolute -top-2 -right-2 w-7 h-7 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold shadow-lg animate-pulse">
                    {item.count}
                  </span>
                )}
              </div>

              {/* Text Content */}
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                {item.title}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {item.description}
              </p>

              {/* Hover Arrow */}
              <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                  <span className="text-gray-600 dark:text-gray-300">→</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Recent Activity Section */}
        <div className="mt-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              Recent Activity
            </h3>
            {recentActivities.length > 0 && (
              <button
                onClick={() => navigate("/activity-history")}
                className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
              >
                View All →
              </button>
            )}
          </div>
          {loadingActivities ? (
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-md border border-gray-100 dark:border-gray-700 flex items-center justify-center">
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading...</p>
            </div>
          ) : recentActivities.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-md border border-gray-100 dark:border-gray-700 flex items-center justify-center">
              <p className="text-sm text-gray-500 dark:text-gray-400">No recent activity</p>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                {(showAllActivities ? recentActivities : recentActivities.slice(0, 3)).map((activity, index) => {
                  const originalIndex = showAllActivities ? index : index;
                  const activityKey = `${activity.reference_id}-${activity.time}`;
                  return (
                    <div
                      key={activityKey}
                      onClick={() => setExpandedActivity(expandedActivity === activityKey ? null : activityKey)}
                      className="bg-white dark:bg-gray-800 rounded-xl p-3 shadow-md border border-gray-100 dark:border-gray-700 hover:shadow-lg transition-all cursor-pointer"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-2 h-2 ${activity.color} rounded-full animate-pulse flex-shrink-0`}
                        ></div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">
                            {activity.action}
                          </p>
                          <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5 truncate">
                            {activity.description}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {activity.time_ago}
                          </p>
                        </div>
                        <div className="flex-shrink-0">
                          {expandedActivity === activityKey ? (
                            <ChevronUp className="w-4 h-4 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                          )}
                        </div>
                      </div>
                      
                      {/* Expanded Details */}
                      {expandedActivity === activityKey && (
                        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700 space-y-1.5">
                          <div className="grid grid-cols-2 gap-2">
                            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2">
                              <p className="text-xs text-gray-500 dark:text-gray-400">Type</p>
                              <p className="text-xs font-medium text-gray-900 dark:text-white capitalize mt-0.5">
                                {activity.type?.replace(/_/g, ' ')}
                              </p>
                            </div>
                            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2">
                              <p className="text-xs text-gray-500 dark:text-gray-400">App ID</p>
                              <p className="text-xs font-medium text-gray-900 dark:text-white mt-0.5">
                                {activity.application_id}
                              </p>
                            </div>
                          </div>
                          
                          {activity.reference_id && activity.reference_id !== activity.application_id && (
                            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2">
                              <p className="text-xs text-gray-500 dark:text-gray-400">Reference ID</p>
                              <p className="text-xs font-medium text-gray-900 dark:text-white mt-0.5">
                                {activity.reference_id}
                              </p>
                            </div>
                          )}
                          
                          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2">
                            <p className="text-xs text-gray-500 dark:text-gray-400">Timestamp</p>
                            <p className="text-xs font-medium text-gray-900 dark:text-white mt-0.5">
                              {new Date(activity.time).toLocaleString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              
              {recentActivities.length > 3 && (
                <button
                  onClick={() => setShowAllActivities(!showAllActivities)}
                  className="w-full mt-3 px-4 py-2.5 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl font-semibold text-sm shadow-lg hover:shadow-xl transition-all active:scale-95"
                >
                  {showAllActivities ? 'Show Less' : `View More (${recentActivities.length - 3} more)`}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default Home;
